import type { AudienceReply } from "../../shared/types.ts";

export type ReplyDensityTier = "large" | "medium" | "compact";

/* Fitting defaults derived from the 1080p budget in the spec
   (docs/superpowers/specs/2026-07-15-projector-thread-reply-fit-design.md);
   tune here after seeing a real projector. */
const MEDIUM_TIER_MIN = 5;
const COMPACT_TIER_MIN = 9;
const TWO_COLUMN_MIN = 7;

export interface ProjectedRepliesLayout {
  /** Highlighted reply pinned first (guaranteeing it is on screen), rest chronological. */
  replies: AudienceReply[];
  tier: ReplyDensityTier;
  columns: 1 | 2;
  /** Publicly shown replies the server window did not send ("+K more replies"). */
  hiddenReplyCount: number;
}

export function deriveProjectedRepliesLayout(args: {
  replies: AudienceReply[];
  showReplies: boolean;
  highlightReplyId?: string;
  replyCount: number;
}): ProjectedRepliesLayout {
  if (!args.showReplies) {
    return { replies: [], tier: "large", columns: 1, hiddenReplyCount: 0 };
  }
  const highlighted = args.highlightReplyId
    ? args.replies.find((reply) => reply.id === args.highlightReplyId)
    : undefined;
  const replies = highlighted
    ? [highlighted, ...args.replies.filter((reply) => reply.id !== highlighted.id)]
    : [...args.replies];
  const count = replies.length;
  return {
    replies,
    tier: count >= COMPACT_TIER_MIN ? "compact" : count >= MEDIUM_TIER_MIN ? "medium" : "large",
    columns: count >= TWO_COLUMN_MIN ? 2 : 1,
    hiddenReplyCount: Math.max(0, args.replyCount - count),
  };
}

export interface ReplyFitStep {
  tier: ReplyDensityTier;
  columns: 1 | 2;
}

export interface ReplyFitEscalation extends ReplyFitStep {
  canEscalate: boolean;
}

const TIER_ORDER: ReplyDensityTier[] = ["large", "medium", "compact"];

/** Successive fit attempts for when the rendered replies overflow the stage:
 *  force two columns first, then step the density down. The final step is
 *  terminal — any remaining overflow fades at the bottom (user decision
 *  2026-07-15: never re-arrange back to one column). */
export function escalateReplyFit(
  base: ProjectedRepliesLayout,
  level: number,
): ReplyFitEscalation {
  const steps: ReplyFitStep[] = [{ tier: base.tier, columns: base.columns }];
  const columns: 1 | 2 = base.replies.length >= 2 ? 2 : base.columns;
  if (columns !== base.columns) steps.push({ tier: base.tier, columns });
  for (
    let next = TIER_ORDER.indexOf(base.tier) + 1;
    next < TIER_ORDER.length;
    next += 1
  ) {
    steps.push({ tier: TIER_ORDER[next], columns });
  }
  const index = Math.min(level, steps.length - 1);
  return { ...steps[index], canEscalate: index < steps.length - 1 };
}
